import { useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  FiMenu,
  FiX,
  FiUser,
  FiLogOut,
  FiShield,
  FiAlertCircle,
} from "react-icons/fi";
import Button from "../common/Button";
import {
  clearSessionExpiredMessage,
  getSessionExpiredMessage,
  getStoredRole,
  hasValidSession,
  logout,
} from "../../services/authService";

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState("");
  const navigate = useNavigate();

  const isAuthenticated = hasValidSession();
  const role = getStoredRole();
  const isAdmin = isAuthenticated && role === "ADMIN";

  useEffect(() => {
    const message = getSessionExpiredMessage();

    if (message) {
      setSessionExpiredMessage(message);
      clearSessionExpiredMessage();
    }
  }, []);

  const navLinks = useMemo(
    () => [
      { to: "/", label: "Home", end: true },
      { to: "/services", label: "Services" },
      { to: "/booking", label: "Book" },
      { to: "/gallery", label: "Gallery" },
      { to: "/contact", label: "Contact" },
    ],
    []
  );

  const linkClass = ({ isActive }) =>
    [
      "transition-colors duration-200 text-sm font-medium",
      isActive ? "text-henna" : "text-[#3b2f2f] hover:text-henna",
    ].join(" ");

  const mobileLinkClass = ({ isActive }) =>
    [
      "block rounded-xl px-4 py-3 text-sm font-medium transition-colors duration-200",
      isActive
        ? "bg-soft-cream text-henna"
        : "text-[#3b2f2f] hover:bg-soft-cream hover:text-henna",
    ].join(" ");

  const handleLogout = async () => {
    await logout();
    setMobileOpen(false);
    navigate("/", { replace: true });
  };

  const closeMobileMenu = () => setMobileOpen(false);

  return (
    <header className="sticky top-0 z-40 border-b border-goldlight bg-white/90 backdrop-blur">
      <div className="container-page">
        <div className="flex min-h-[72px] items-center justify-between gap-4">
          <NavLink
            to="/"
            onClick={closeMobileMenu}
            className="shrink-0 text-2xl font-semibold tracking-tight text-henna"
          >
            Nayra Henna
          </NavLink>

          <nav className="hidden items-center gap-6 lg:flex">
            {navLinks.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={linkClass}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            {isAuthenticated ? (
              <>
                <NavLink to="/profile">
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<FiUser />}
                  >
                    My Account
                  </Button>
                </NavLink>

                {isAdmin && (
                  <NavLink to="/admin">
                    <Button
                      variant="outline"
                      size="sm"
                      leftIcon={<FiShield />}
                    >
                      Admin Dashboard
                    </Button>
                  </NavLink>
                )}

                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<FiLogOut />}
                  onClick={handleLogout}
                >
                  Sign Out
                </Button>
              </>
            ) : (
              <>
                <NavLink to="/login">
                  <Button variant="secondary" size="sm">
                    Sign In
                  </Button>
                </NavLink>

                <NavLink to="/register">
                  <Button variant="primary" size="sm">
                    Create Account
                  </Button>
                </NavLink>
              </>
            )}
          </div>

          <button
            type="button"
            aria-label="Toggle navigation"
            onClick={() => setMobileOpen((prev) => !prev)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-goldlight bg-white text-[#3b2f2f] transition hover:bg-soft-cream lg:hidden"
          >
            {mobileOpen ? <FiX size={20} /> : <FiMenu size={20} />}
          </button>
        </div>

        {sessionExpiredMessage ? (
          <div className="pb-4">
            <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <FiAlertCircle className="mt-0.5 shrink-0" />
              <span>{sessionExpiredMessage}</span>
            </div>
          </div>
        ) : null}

        {mobileOpen && (
          <div className="border-t border-goldlight py-4 lg:hidden">
            <nav className="flex flex-col gap-2">
              {navLinks.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={closeMobileMenu}
                  className={mobileLinkClass}
                >
                  {item.label}
                </NavLink>
              ))}

              <div className="mt-3 grid gap-2">
                {isAuthenticated ? (
                  <>
                    <NavLink
                      to="/profile"
                      onClick={closeMobileMenu}
                      className={mobileLinkClass}
                    >
                      My Account
                    </NavLink>

                    {isAdmin ? (
                      <NavLink
                        to="/admin"
                        onClick={closeMobileMenu}
                        className={mobileLinkClass}
                      >
                        Admin Dashboard
                      </NavLink>
                    ) : null}

                    <button
                      type="button"
                      onClick={handleLogout}
                      className="rounded-xl border border-goldlight px-4 py-3 text-left text-sm font-medium text-[#3b2f2f] transition hover:bg-soft-cream hover:text-henna"
                    >
                      Sign Out
                    </button>
                  </>
                ) : (
                  <>
                    <NavLink
                      to="/login"
                      onClick={closeMobileMenu}
                      className={mobileLinkClass}
                    >
                      Sign In
                    </NavLink>
                    <NavLink
                      to="/register"
                      onClick={closeMobileMenu}
                      className={mobileLinkClass}
                    >
                      Create Account
                    </NavLink>
                  </>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;